import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Loader2, User, ChevronDown, ChevronUp, 
  Users, Calendar, Award, RefreshCw, Trash2, MessageSquare, UserPlus, Star, Trophy, Medal 
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getFrequentPlayers, FrequentPlayer } from '../lib/game/sessionService';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserProfile } from '@/types';

interface FrequentPlayersListProps {
  gameId?: string;
  limit?: number;
}

export default function FrequentPlayersList({ gameId, limit = 5 }: FrequentPlayersListProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [players, setPlayers] = useState<FrequentPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  const [userProfiles, setUserProfiles] = useState<Record<string, { username: string, avatar_url?: string, full_name?: string }>>({});
  const [refreshing, setRefreshing] = useState(false);

  const loadFrequentPlayers = useCallback(async () => {
    if (!user) {
      setError('Usuário não autenticado.');
      setLoading(false);
      setPlayers([]);
      setUserProfiles({});
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const playersData = await getFrequentPlayers(user.id);

      console.log("Jogadores frequentes carregados (via serviço):", playersData);

      const limitedPlayers = playersData ? playersData.slice(0, limit) : [];
      setPlayers(limitedPlayers);

      const userIds = limitedPlayers
        .map(player => player.userId)
        .filter((id): id is string => typeof id === 'string' && id !== '');

      console.log('IDs de usuários válidos para buscar perfis:', userIds);

      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, avatar_url, full_name')
          .in('id', userIds);

        if (profilesError) throw profilesError;

        const profileMap = (profilesData || []).reduce((acc, profile) => {
          if (profile.id) {
            acc[profile.id] = {
              username: profile.username ?? 'Usuário Desconhecido',
              full_name: profile.full_name ?? profile.username ?? 'Usuário Desconhecido',
              avatar_url: profile.avatar_url ?? undefined,
            };
          }
          return acc;
        }, {} as Record<string, { username: string, avatar_url?: string, full_name?: string }>);
        setUserProfiles(profileMap);
      }

    } catch (err: any) {
      console.error("Erro ao buscar jogadores frequentes:", err);
      setError(err.message || 'Falha ao buscar jogadores frequentes');
      setPlayers([]);
      setUserProfiles({});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, limit]);

  useEffect(() => {
    loadFrequentPlayers();
  }, [loadFrequentPlayers]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadFrequentPlayers();
  };

  const togglePlayerDetails = (playerId: string) => {
    if (expandedPlayerId === playerId) {
      setExpandedPlayerId(null);
    } else {
      setExpandedPlayerId(playerId);
    }
  };

  const getAvatarUrl = (userId?: string): string | undefined => {
    if (!userId) return undefined;
    return userProfiles[userId]?.avatar_url;
  };

  const getProfilePath = (userId?: string): string => {
    if (!userId) return '#';
    const profile = userProfiles[userId];
    return profile?.username ? `/profile/${profile.username}` : `/profile/${userId}`;
  };

  const handleViewProfile = (userId?: string) => {
    if (!userId) return;
    const path = getProfilePath(userId);
    if (path !== '#') {
      navigate(path);
    }
  };

  const handleSendMessage = (userId?: string) => {
    if (!userId) return;
    const profile = userProfiles[userId];
    if (profile?.username) {
      navigate(`/messages/${profile.username}`);
    }
  };

  const getOrdinalSuffix = (n: number) => {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Jogadores Frequentes</CardTitle>
          <CardDescription>Carregando jogadores com quem você mais joga...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <Skeleton className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Jogadores Frequentes</CardTitle>
          <CardDescription>Não foi possível carregar os jogadores</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-destructive">{error}</div>
          <Button onClick={handleRefresh} variant="outline" className="mt-3" disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Tentar novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (players.length === 0) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Jogadores Frequentes</CardTitle>
          <CardDescription>Ainda não há jogadores frequentes para mostrar</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-sm mb-4 text-center">
              Quando você começar a jogar mais partidas, os jogadores mais frequentes aparecerão aqui.
            </p>
            <Button onClick={handleRefresh} variant="outline" disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 bg-white dark:bg-gray-800 border dark:border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-xl text-gray-900 dark:text-gray-100">Jogadores Frequentes</CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">Veja com quem você mais joga e suas estatísticas</CardDescription>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm" disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {players.map((player, index) => {
            const profile = player.userId ? userProfiles[player.userId] : null;
            const avatarUrl = profile?.avatar_url;
            const profilePath = getProfilePath(player.userId);
            const rank = index + 1;
            const medalColor = rank === 1 ? "text-yellow-400 dark:text-yellow-300" : rank === 2 ? "text-gray-400 dark:text-gray-500" : rank === 3 ? "text-orange-500 dark:text-orange-400" : "text-gray-500 dark:text-gray-400";
            const MedalIcon = rank === 1 ? Trophy : Medal;

            return (
              <li key={player.playerId} className="border dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div 
                  className={`flex items-center justify-between cursor-pointer ${profile ? '' : 'opacity-50'}`}
                  onClick={() => profile && togglePlayerDetails(player.playerId)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center w-8">
                      {rank <= 3 ? (
                        <MedalIcon className={`h-6 w-6 ${medalColor}`} />
                      ) : (
                        <span className={`text-lg font-semibold ${medalColor}`}>{rank}<span className="text-xs">{getOrdinalSuffix(rank)}</span></span>
                      )}
                    </div>
                    <div>
                      <Link to={profilePath} className={`font-semibold hover:text-primary ${profile ? 'text-gray-800 dark:text-gray-200' : 'opacity-50'}`}>
                        {profile?.full_name || player.playerName}
                      </Link>
                      {profile?.full_name && (
                        <div className="text-xs text-muted-foreground dark:text-gray-400">
                          @{profile?.username}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground dark:text-gray-400">
                        <span>{player.playCount} partida(s)</span>
                        <span>•</span>
                        <span>{player.winRate?.toFixed(0) || 'N/A'}% vitórias</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-5">
                    {expandedPlayerId === player.playerId ? 
                      <ChevronUp className="h-5 w-5 text-muted-foreground dark:text-gray-400" /> : 
                      <ChevronDown className="h-5 w-5 text-muted-foreground dark:text-gray-400" />
                    }
                  </div>
                </div>

                {expandedPlayerId === player.playerId && profile && (
                  <div className="mt-4 space-y-3">
                    <Separator className="bg-gray-200 dark:bg-gray-600" />
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="font-medium text-muted-foreground dark:text-gray-400">Vitórias:</p>
                        <p className="text-green-600 dark:text-green-400 font-semibold">{player.wins}</p>
                      </div>
                      <div>
                        <p className="font-medium text-muted-foreground dark:text-gray-400">Derrotas:</p>
                        <p className="text-red-600 dark:text-red-400 font-semibold">{player.losses}</p>
                      </div>
                      
                      <div className="col-span-2 mt-1">
                        <div className="flex items-center">
                          <Award className="h-4 w-4 text-amber-500 dark:text-amber-400 mr-2" />
                          <p className="font-medium text-gray-700 dark:text-gray-300">Jogo mais jogado:</p>
                        </div>
                        <Link to={`/games/${player.mostPlayedGameId}`} className="text-primary hover:underline ml-6">
                          {player.mostPlayedGame} ({player.playCount} vezes)
                        </Link>
                      </div>
                      
                      {player.gamesTogether > 0 && (
                        <div className="col-span-2 mt-1">
                          <div className="flex items-center">
                            <Users className="h-4 w-4 text-blue-500 dark:text-blue-400 mr-2" />
                            <p className="font-medium text-gray-700 dark:text-gray-300">Sessões compartilhadas:</p>
                          </div>
                          <p className="ml-6 text-gray-600 dark:text-gray-400">{player.gamesTogether} partidas que {player.userId ? 'você foi incluído' : 'você registrou'}</p>
                        </div>
                      )}
                      
                      <div className="col-span-2 mt-1">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-indigo-500 dark:text-indigo-400 mr-2" />
                          <p className="font-medium text-gray-700 dark:text-gray-300">Última partida:</p>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Última partida: {player.lastPlayed ? format(new Date(player.lastPlayed), "dd/MM/yyyy", { locale: ptBR }) : 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                    <Separator className="bg-gray-200 dark:bg-gray-600" />
                    
                    <div className="flex justify-end gap-2">
                      {player.userId && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProfile(player.userId);
                            }}
                          >
                            <User className="h-4 w-4 mr-1" />
                            Ver Perfil
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendMessage(player.userId);
                            }}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Mensagem
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        
        {players.length >= limit! && (
          <div className="mt-4 text-center">
            <Button onClick={() => navigate('/connections')} variant="link" className="text-sm">
              <Users className="h-4 w-4 mr-1" />
              Ver todos os jogadores
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 